import { groupRelativePlayingHandicap } from '@/lib/games/handicap';
import { strokesOnForHole } from '@/lib/games/live-engine-shared';
import type { CourseHole } from '@/lib/db/types';
import type { RoundPlayer, WolfHoleState, WolfRoundConfig } from '@/lib/games/engine/types';

export type WolfHoleMode = 'blind' | 'lone' | 'partner' | null;

export interface WolfMultipliers {
  partnerWin: number;
  loneWin: number;
  blindWin: number;
  packWinPartner: number;
  packWinLone: number;
  packWinBlind: number;
}

export const DEFAULT_WOLF_MULTIPLIERS: WolfMultipliers = {
  partnerWin: 1,
  loneWin: 2,
  blindWin: 3,
  packWinPartner: 1,
  packWinLone: 1,
  packWinBlind: 1,
};

export const DEFAULT_WOLF_ROUND_CONFIG: WolfRoundConfig = {
  blindWolfEnabled: false,
  blindMoneyMultiplier: 2,
  blindPartnerRule: 'first-tee',
  multipliers: { ...DEFAULT_WOLF_MULTIPLIERS },
};

/** Rotating tee order — shifts each hole. Wolf is first in tee order. */
export function teeOrderForHole(holeIx: number, playerCount = 4): number[] {
  const base = Array.from({ length: playerCount }, (_, i) => i);
  const shift = holeIx % playerCount;
  return [...base.slice(shift), ...base.slice(0, shift)];
}

/** @deprecated use wolfPlayerIndex */
export function wolfIndex(holeIx: number): number {
  return wolfPlayerIndex(holeIx);
}

export function wolfPlayerIndex(holeIx: number, playerCount = 4): number {
  return teeOrderForHole(holeIx, playerCount)[0];
}

/** Non-wolf players in tee order (pick / pass sequence). */
export function nonWolvesInTeeOrder(holeIx: number, playerCount = 4): number[] {
  const wolf = wolfPlayerIndex(holeIx, playerCount);
  return teeOrderForHole(holeIx, playerCount).filter((i) => i !== wolf);
}

export function mergeWolfConfig(raw?: WolfRoundConfig | null): WolfRoundConfig {
  return {
    ...DEFAULT_WOLF_ROUND_CONFIG,
    ...raw,
    multipliers: { ...DEFAULT_WOLF_MULTIPLIERS, ...raw?.multipliers },
  };
}

export function mergeWolfMultipliers(raw?: Partial<WolfMultipliers>): WolfMultipliers {
  return { ...DEFAULT_WOLF_MULTIPLIERS, ...raw };
}

function legacyPartnerIndex(hs: WolfHoleState): number | null {
  if (hs.partnerIndex != null) return hs.partnerIndex;
  if (typeof hs.decision === 'number') return hs.decision;
  return null;
}

/** Resolve blind / lone / partner for a hole from stored state. */
export function resolveWolfHoleMode(
  hs: WolfHoleState | undefined,
  holeIx: number,
  wolfConfig: WolfRoundConfig,
): { mode: WolfHoleMode; partnerIndex: number | null; locked: boolean } {
  if (!hs) return { mode: null, partnerIndex: null, locked: false };

  const pickOrder = nonWolvesInTeeOrder(holeIx);
  const passes = hs.passes ?? [];
  const partner = legacyPartnerIndex(hs);

  if (hs.blindDeclared || hs.mode === 'blind' || hs.decision === 'blind') {
    const autoPartner = pickOrder[0];
    const partnerIndex = partner ?? (wolfConfig.blindPartnerRule === 'first-tee' ? autoPartner : partner);
    return { mode: 'blind', partnerIndex, locked: true };
  }

  if (hs.mode === 'lone' || hs.decision === 'lone') {
    return { mode: 'lone', partnerIndex: null, locked: !!hs.locked };
  }

  if (partner != null) {
    return { mode: 'partner', partnerIndex: partner, locked: !!hs.locked };
  }

  if (passes.length >= pickOrder.length) {
    return { mode: 'lone', partnerIndex: null, locked: true };
  }

  return { mode: null, partnerIndex: null, locked: false };
}

export interface WolfHoleLedgerEntry {
  hole: number;
  wolfIndex: number;
  mode: WolfHoleMode;
  partnerIndex: number | null;
  holeWinner: 'wolf' | 'pack' | 'tie';
  pointsByPlayerIndex: number[];
  summary: string;
}

export function computeWolfHolePoints(
  holeIx: number,
  gross: number[],
  players: RoundPlayer[],
  holes: CourseHole[],
  holeState: WolfHoleState | undefined,
  wolfConfig: WolfRoundConfig,
  handicapAllowance?: number | null,
): { points: number[]; summary: string; mode: WolfHoleMode; partnerIndex: number | null; holeWinner: 'wolf' | 'pack' | 'tie' } {
  const wolfK = wolfPlayerIndex(holeIx);
  const { mode, partnerIndex } = resolveWolfHoleMode(holeState, holeIx, wolfConfig);
  const mult = mergeWolfMultipliers(wolfConfig.multipliers);

  const strokesOn = (k: number) => {
    const player = players[k];
    if (!player) return 0;
    const ph = groupRelativePlayingHandicap(players, player, 'wolf', handicapAllowance);
    return strokesOnForHole(ph, holes[holeIx]?.stroke_index ?? holeIx + 1);
  };

  const np = [0, 0, 0, 0];
  if (mode == null) {
    return { points: np, summary: 'Awaiting Wolf decision', mode, partnerIndex, holeWinner: 'tie' };
  }

  let wsArr: number[];
  if (mode === 'lone') {
    wsArr = [wolfK];
  } else if (mode === 'blind') {
    const pIx = partnerIndex ?? nonWolvesInTeeOrder(holeIx)[0];
    wsArr = [wolfK, pIx];
  } else if (mode === 'partner') {
    if (partnerIndex == null) {
      return { points: np, summary: 'Pick a partner', mode, partnerIndex, holeWinner: 'tie' };
    }
    wsArr = [wolfK, partnerIndex];
  } else {
    return { points: np, summary: 'Awaiting Wolf decision', mode, partnerIndex, holeWinner: 'tie' };
  }

  const osArr = [0, 1, 2, 3].filter((k) => !wsArr.includes(k));
  const wn = Math.min(...wsArr.map((k) => (gross[k] ?? 99) - strokesOn(k)));
  const on = Math.min(...osArr.map((k) => (gross[k] ?? 99) - strokesOn(k)));

  let summary: string;
  let holeWinner: 'wolf' | 'pack' | 'tie' = 'tie';

  if (wn < on) {
    holeWinner = 'wolf';
    if (mode === 'lone') {
      np[wolfK] = mult.loneWin;
      summary = `${players[wolfK]?.name ?? 'Wolf'} wins Lone Wolf · +${mult.loneWin}`;
    } else if (mode === 'blind') {
      wsArr.forEach((k) => { np[k] = mult.blindWin; });
      summary = `Blind Wolf side wins · +${mult.blindWin} each`;
    } else {
      wsArr.forEach((k) => { np[k] = mult.partnerWin; });
      summary = 'Wolf side wins · +' + mult.partnerWin + ' each';
    }
  } else if (on < wn) {
    holeWinner = 'pack';
    const packPts = mode === 'blind' ? mult.packWinBlind : mode === 'lone' ? mult.packWinLone : mult.packWinPartner;
    osArr.forEach((k) => { np[k] = packPts; });
    summary = mode === 'lone'
      ? `The Pack wins · +${packPts} each`
      : mode === 'blind'
        ? `Pack beats Blind Wolf · +${packPts} each`
        : `Pack wins · +${packPts} each`;
  } else {
    summary = 'Halved — no points';
  }

  return { points: np, summary, mode, partnerIndex, holeWinner };
}

export function computeWolfTotals(
  players: RoundPlayer[],
  scores: Record<string, (number | null)[]>,
  holes: CourseHole[],
  holeState: Record<number, WolfHoleState> = {},
  wolfConfig: WolfRoundConfig = DEFAULT_WOLF_ROUND_CONFIG,
  handicapAllowance?: number | null,
): {
  totals: Record<string, number>;
  log: WolfHoleLedgerEntry[];
  wolfConfig: WolfRoundConfig;
} {
  const cfg = mergeWolfConfig(wolfConfig);
  const keys = players.map((p) => p.playerKey);
  const totals: Record<string, number> = {};
  keys.forEach((k) => { totals[k] = 0; });
  const log: WolfHoleLedgerEntry[] = [];
  const grossByPlayer = keys.map((k) => scores[k] || Array(18).fill(null));

  holes.forEach((_, hi) => {
    const gross = keys.map((_, k) => grossByPlayer[k][hi] ?? 99);
    if (gross.some((g) => g === 99)) return;
    const hs = holeState[hi + 1] ?? holeState[hi];
    const { points, summary, mode, partnerIndex, holeWinner } = computeWolfHolePoints(
      hi, gross as number[], players, holes, hs, cfg, handicapAllowance,
    );
    keys.forEach((k, i) => { totals[k] += points[i]; });
    if (mode != null) {
      log.push({
        hole: hi + 1,
        wolfIndex: wolfPlayerIndex(hi),
        mode,
        partnerIndex,
        holeWinner,
        pointsByPlayerIndex: points,
        summary,
      });
    }
  });

  return { totals, log, wolfConfig: cfg };
}

/** Current pick step index (0–2) or -1 if done / blind / lone locked. */
export function wolfPickStep(hs: WolfHoleState | undefined, holeIx: number): number {
  const { mode, locked } = resolveWolfHoleMode(hs, holeIx, DEFAULT_WOLF_ROUND_CONFIG);
  if (locked || mode != null) return -1;
  const pickOrder = nonWolvesInTeeOrder(holeIx);
  const passes = hs?.passes ?? [];
  if (legacyPartnerIndex(hs ?? {}) != null) return -1;
  for (let i = 0; i < pickOrder.length; i++) {
    if (!passes.includes(pickOrder[i])) return i;
  }
  return -1;
}

export function canDeclareBlindOnHole(
  wolfConfig: WolfRoundConfig,
  hs: WolfHoleState | undefined,
  holeIx: number,
): boolean {
  if (!wolfConfig.blindWolfEnabled) return false;
  if (hs?.blindDeclared || hs?.locked) return false;
  const { mode } = resolveWolfHoleMode(hs, holeIx, wolfConfig);
  return mode == null && legacyPartnerIndex(hs ?? {}) == null;
}
